import { Flex, DatePicker, Table } from 'antd'
import { useState } from 'react'

import { useTreatmentServiceGetTreatmentPlanListEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  TreatmentPlanSortBy
} from 'src/api/requests'
import {
  PageBreadcrumbs,
  Select,
  ClassifierInfiniteSelect
} from 'src/components'
import {
  useTreatmentConfig,
  useTreatmentColumns
} from 'src/features/persons/components'
import { useTablePagination, useClassifierId, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import {
  normalizeOrdering,
  getOrderingDirection,
  resetPagination
} from 'src/utils'

export const InstitutionTreatmentsPage = () => {
  const { t } = useLocales()

  const pagination = useTablePagination()
  const { userData } = useUser()

  const { options } = useTreatmentConfig()
  const { columns } = useTreatmentColumns(true)

  const [treatmentType, setTreatmentType] = useState('')
  const [date, setDate] = useState<string[]>([])
  const [ordering, setOrdering] = useState('')

  const classifierTreatmentType = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    legalEntityId: userData?.currentLegalEntityId,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as TreatmentPlanSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(treatmentType && { treatmentType }),
    ...(date?.[0] && date?.[1] && { startDate: date?.[0], endDate: date?.[1] })
  }

  const { data, isLoading } =
    useTreatmentServiceGetTreatmentPlanListEndpoint(queryParams)

  const onDateChange = (datesString: string[]) => {
    setDate(datesString)
    resetPagination(pagination)
  }

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onTreatmentSelect = (val: { label: string }) => {
    setTreatmentType(val.label)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      title={t('persons.person_treatments')}
      items={[{ title: t('persons.person_treatments') }]}
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          <DatePicker.RangePicker
            format={[dayMonthYearBackSlash, dayMonthYearBackSlash]}
            onChange={(_, datesString) => onDateChange(datesString)}
            placeholder={[
              t('notifications.from_date'),
              t('notifications.to_date')
            ]}
          />

          <ClassifierInfiniteSelect
            labelInValue
            onSelect={onTreatmentSelect}
            onClear={() => setTreatmentType('')}
            id={classifierTreatmentType}
            placeholder={t('treatments.treatment_type')}
            style={{
              minWidth: 200
            }}
          />

          <Select onSelect={onSelect} options={options} />
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
